<template>
  <!-- BEGIN: Lead View -->
  <div class="col-span-12">
    <div class="grid grid-cols-12 gap-6">
      <!--  Begin: Header -->
      <div class="col-span-12 md:flex space-x-4 items-center justify-between">
        <div class="flex space-x-4 items-center">
          <button v-if="canEdit" type="button" class="btn btn-sm btn-primary p-0" @click.prevent="toggleEditMode('first_name', false)">
            <svg width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="28" height="28" rx="7" fill="var(--color-primary)"/>
              <path v-if="editMode"
                    d="m15.416 14.004 4.3-4.29a1.004 1.004 0 1 0-1.42-1.42l-4.29 4.3-4.29-4.3a1.004 1.004 0 0 0-1.42 1.42l4.3 4.29-4.3 4.29a1 1 0 0 0 .325 1.639 1 1 0 0 0 1.095-.219l4.29-4.3 4.29 4.3a1.002 1.002 0 0 0 1.639-.325 1 1 0 0 0-.22-1.095l-4.3-4.29Z"
                    fill="#fff"/>
              <path v-else
                    d="m16.505 10.249-7.97 7.97-.522 1.778 1.742-.503 7.997-7.997-1.247-1.248Zm1.36-1.36 1.247 1.247 1.162-1.162a.44.44 0 0 0 0-.624l-.624-.623a.44.44 0 0 0-.624 0l-1.16 1.162h-.001Zm3.033-2.41.623.624a2.205 2.205 0 0 1 0 3.118l-10.84 10.84-3.721 1.076a.88.88 0 0 1-1.091-1.094l1.1-3.752L17.781 6.479a2.205 2.205 0 0 1 3.118 0h-.001Z"
                    fill="#fff"/>
            </svg>
          </button>
          <h4 class="text-3xl font-semibold">
            Viewing {{ lead.lead_category === 'recruitment' ? 'Recruitment' : '' }} Lead - <span class="text-3xl font-semibold">{{ lead.display_name }} ({{ lead.id }})
            <span v-if="usePage().props.value.auth?.user?.id === 493 && lead.legacy_id">(<a :href="`https://topbrokercrm.com/lead.asp?leadid=${lead.legacy_id}`" target="_blank" class="text-blue-600">{{ lead.legacy_id }}</a>)</span>
            <span v-if="lead.lead_category === 'commercial'"> | <span class="text-blue-800">Rep Agency: {{ lead.representing_agency ?? '	— ' }}</span></span>
            </span>
          </h4>
        </div>

        <div class="block md:flex space-y-2 mt-2 md:mt-0 md:space-y-0 md:space-x-2 ">
          <button v-if="canCreateNote" @click="createLeadNoteModalVisible = true" type="button" class="w-full whitespace-nowrap md:w-auto btn-primary inline-flex items-center px-5 py-2 text-xl font-black">
            <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Add Note
          </button>
          <button v-if="isAdmin || isRecruiterAdmin" @click.prevent="markLeadAsDNC" type="button" class="w-full md:w-auto btn-danger text-xl font-black">DNC</button>
          <button v-if="isAdmin" @click.prevent="confirmDelete" type="button" class="w-full md:w-auto btn-danger text-xl font-black">Delete</button>
        </div>
      </div>

      <div class="bg-red-400 border border-red-400 text-red-700 px-4 py-3 rounded relative col-span-12" role="alert" v-if="data_hst_age_locked">
        <p>
          <strong class="font-bold">Lead is HST Aged Locked</strong>
        </p>
        <ul class="list-disc px-8">
          <li>Email addresses and phone numbers have been removed</li>
          <li>All open actions have been closed</li>
        </ul>
        <p>
          <span class="block sm:inline"></span>
        </p>
      </div>

      <div class="bg-red-400 border border-red-400 text-red-700 px-4 py-3 rounded relative col-span-12" role="alert" v-if="isDnc">
        <p>
          <strong class="font-bold">Lead is DNC</strong>
        </p>
        <ul class="list-disc px-8">
          <li>Email addresses and phone numbers have been removed</li>
        </ul>
        <p>
          <span class="block sm:inline"></span>
        </p>
      </div>
      <!-- End: Header -->

      <!-- Tabs -->
      <div class="col-span-12 flex items-center justify-between">
        <div class="lg:hidden w-full">
          <label for="tabs" class="sr-only">Select a tab</label>
          <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
          <select @change="setCurrentTab($event.target.value)" id="tabs" name="tabs"
                  class="block w-full rounded-md border-gray-300 focus:border-tb-blue focus:ring-tb-blue">
            <option v-for="tab in visibleTabs" :key="tab.key" :value="tab.key" :selected="currentTab === tab.key">{{
                tab.label
              }}
            </option>
          </select>
        </div>
        <div class="hidden lg:block w-full">
          <nav class="flex space-x-1 shadow ring-1 ring-black ring-opacity-5 md:rounded-lg p-2 bg-white" aria-label="Tabs">
            <a @click.prevent="setCurrentTab(tab.key)" v-for="tab in visibleTabs" :key="tab.key" href="javascript:;"
               :class="[currentTab === tab.key ? 'bg-tb-blue text-white shadow' : 'text-gray-500 hover:text-gray-700', 'px-6 py-2 font-semibold rounded-md']"
               :aria-current="currentTab === tab.key ? 'page' : undefined">{{ tab.label }}</a>
          </nav>
        </div>
      </div>
    </div>
  </div>

  <div class="col-span-12 xl:col-span-8">
    <div class="grid grid-cols-12 gap-6">
      <div class="col-span-12 rounded-lg">
        <div class="tab-content min-h-[300px]">
          <div v-if="currentTab === tabs[0].key" :id="tabs[0].key" role="tabpanel" :aria-labelledby="tabs[0].key">
            <lead-view-info
              ref="lead-view-info-tab"
              v-model:lead="lead"
              :states="states"
              :edit-mode="editMode"
              :lead-dependants="dependants"
              :clicktocall="clicktocall"
              @show-edit-mode="handleShowEditMode"
              @close-edit-mode="toggleEditMode"
              @show-compose-sms-form="showComposeSmsForm"
              @show-compose-email-form="showComposeEmailForm"
              @refresh-dependants="handleRefreshDependants"
              :hst_age_locked="data_hst_age_locked"
              :lead-parent-options="leadParentOptions"
              :languages="languages"
              :reported-external-dnc-phone-1="reportedExternalDncPhone1"
              :reported-external-dnc-phone-2="reportedExternalDncPhone2"
            >
            </lead-view-info>

            <div v-if="lead.lead_category === 'commercial'">
              <div class="px-2 mt-2">
                <LeadDependantsTable
                  :lead-dependants="dependants"
                  :show-details-button="lead.has_type_marked_as_group"
                  :can-edit="canEdit"
                  @show-edit-form="showDependantEditForm"
                  @show-create-form="dependantModalVisible = true"
                ></LeadDependantsTable>
              </div>
            </div>
          </div>
          <div v-else-if="currentTab === tabs[1].key" :id="tabs[1].key" class="tab-pane leading-relaxed"
               role="tabpanel" :aria-labelledby="tabs[1].key">
            <LeadActionHistoryTab
              :roles="roles"
              :lead="lead"
              :next-lead-schedule-list="nextLeadScheduleList"
              :lead-action-history="leadActionHistory"
              :hst-action-sync-statuses="hstActionSyncStatuses"
              @performExtraOption="performExtraOption"
              @showEmailMessageContent="showEmailMessageContent"
              @showEmailTemplateContent="showEmailTemplateContent"
              @showGracePullDetails="showGracePullDetails"
            ></LeadActionHistoryTab>
          </div>
          <div v-else-if="currentTab === tabs[3].key" :id="tabs[3].key" class="tab-pane leading-relaxed px-2"
               role="tabpanel" :aria-labelledby="tabs[2].key">
             <LeadSalesTable
               :loading="leadSalesLoading"
               :loaded="leadSalesLoaded"
               :active-tab="currentTab"
               :total-premium="totalPremium"
               :hst_available="hst_available"
               :carriers-data="carriersData"
               :applicants="applicants"
               :products="products"
               :enrollees="enrollees"
               :split-percentages="splitPercentages"
               :agents="agents"
               :policies="policies"
               :lead-id="lead.id"
               :lead="lead"
               :next-lead-schedules-list="nextLeadScheduleList"
               :user="user"
               :canEdit="canEdit"
             ></LeadSalesTable>
          </div>
          <div v-else-if="currentTab === tabs[2].key" :id="tabs[2].key" class="tab-pane leading-relaxed px-2"
               role="tabpanel" :aria-labelledby="tabs[3].key">
            <lead-view-quotesheets :quotesheets="quotesheets" :lead_id="lead.id"></lead-view-quotesheets>
            <!-- Quotesheet AI suggested products: second pane below the quotesheets list
                 (self-hides when the quote-match feature is off for the agency). -->
            <div class="mt-6">
              <AppQuoteMatchCard :lead="lead" />
            </div>
          </div>
          <div v-else-if="currentTab === tabs[4].key" :id="tabs[4].key" class="tab-pane leading-relaxed p-2 md:p-5 bg-white rounded-lg border"
               role="tabpanel" :aria-labelledby="tabs[4].key">
            <lead-view-files :lead-id="lead.id"></lead-view-files>
          </div>
          <div v-else-if="currentTab === tabs[5].key" :id="tabs[5].key" class="tab-pane leading-relaxed p-2 md:p-5 bg-white rounded-lg border"
               role="tabpanel" :aria-labelledby="tabs[5].key">
            <LeadLoginTable :lead="lead" :logins="logins"></LeadLoginTable>
          </div>
          <div v-else-if="currentTab === 'questionnaires'" id="questionnaires" class="tab-pane leading-relaxed px-2 md:px-2 bg-white rounded-lg border"
               role="tabpanel" aria-labelledby="questionnaires">
            <LeadQuestionnairesTable :lead="lead" :questionnaires="questionnaires"></LeadQuestionnairesTable>
          </div>

          <div v-else-if="currentTab === 'medications'" id="medications" class="px-2 tab-pane leading-relaxed"
              role="tabpanel" aria-labelledby="medications">
            <LeadMedicationsAndDoctors :lead="lead" :lead-dependants="dependants"></LeadMedicationsAndDoctors>
          </div>

          <div v-else-if="currentTab === 'lead-billing'" id="lead-billing" class="tab-pane leading-relaxed bg-white rounded-lg border"
            role="tabpanel" aria-labelledby="lead-billing">
            <LeadBillingForm :lead="lead" :lead-billing-info="leadBillingInfo" :states="states"></LeadBillingForm>
          </div>
          <div v-else-if="currentTab === 'child-leads'" id="child-leads" class="tab-pane leading-relaxed bg-white rounded-lg border"
            role="tabpanel" aria-labelledby="child-leads">
            <LeadChildLeadsTable :lead="lead" :child-leads="childLeads.data"></LeadChildLeadsTable>
          </div>
          <div v-else-if="currentTab === 'campaigns'" id="campaigns" class="tab-pane leading-relaxed"
            role="tabpanel" aria-labelledby="campaigns">
            <LeadCampaigns :lead="lead" :child-leads="childLeads.data"
                :lead-campaigns="leadCampaigns"
                :lead-campaigns-global-exclusion="leadCampaignsGlobalExclusion"
                :lead-opted-out-of-agent-emails="leadOptedOutOfAgentEmails"
            ></LeadCampaigns>
          </div>
          <div v-else-if="currentTab === 'forms'" id="forms" class="tab-pane leading-relaxed"
            role="tabpanel" aria-labelledby="forms">
            <LeadIntakeForms :lead="lead"></LeadIntakeForms>
          </div>
          <div v-else-if="currentTab === 'state-licenses'" id="state-licenses" class="tab-pane leading-relaxed bg-white rounded-lg border"
            role="tabpanel" aria-labelledby="state-licenses">
            <div class="p-8">
              <agent-and-lead-states ref="lead-states" model-type="lead" :states="states" :model-id="lead.id"></agent-and-lead-states>
            </div>
          </div>
          <div v-else-if="currentTab === 'carriers'" id="carriers" class="tab-pane leading-relaxed bg-white rounded-lg border"
            role="tabpanel" aria-labelledby="carriers">
            <div class="py-4">
              <agent-and-lead-carriers model-type="lead" :model-id="lead.id" :agency-carriers="agency_carriers"></agent-and-lead-carriers>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
  <!-- END: Lead View -->


  <!-- Create Dependant Modal -->
  <Modal :show="dependantModalVisible" @onClose="closeDependantFormModal" :title="`Viewing lead ${lead.display_name } (${lead.id})`">
    <p class="mt-2 text-center">{{dependantToEdit !== null ? 'Update' : 'Create '}} Dependant</p>
    <LeadDependantForm
      :lead="lead"
      :dependant-roles="dependantRoles"
      :lead_id="lead.id"
      @dependantsUpdated="handleDependantsUpdated"
      :edit-mode="dependantToEdit !== null"
      :dependant-to-edit="dependantToEdit"
      :lead-last-name="lead.last_name"
      :lead-dependants="dependants"
      :canEdit="canEdit"
      :dependant-company-roles="dependantCompanyRoles"
    ></LeadDependantForm>
  </Modal>

  <!-- Create Note Modal -->
  <Modal :show="createLeadNoteModalVisible" @onClose="createLeadNoteModalVisible = false" :title="`Viewing lead ${lead.display_name } (${lead.id})`">
    <p class="mt-2 text-center">Add a note</p>
    <LeadViewNoteForm :lead-id="lead.id" @note-added="handleNoteAdded"></LeadViewNoteForm>
  </Modal>
  <!-- Action Tab Email Message Modal -->
  <Modal :show="showEmailContentModal" @onClose="closeEmailContentModal" title="Viewing Email Content">
      <div class="flex items-center justify-center pb-3 mt-10" v-if="fetchingEmailContent">
          <LoadingSpinner color-class="text-tb-blue"></LoadingSpinner>
      </div>
      <div v-else class="mt-10 max-w-fit overflow-auto">
          <p class="text-gray-700">Subject:</p>
          <p class="font-semibold">{{ this.emailContent?.subject }}</p>
          <p class="mt-4 xt-gray-7">Email Content:</p>
          <p class="" v-html="this.emailContent?.body"></p>
      </div>

  </Modal>


  <Modal :show="showGracePullDetailsModal" @onClose="closeGracePullDetails" title="Viewing Grace Pull Details">
    <div class="flex items-center justify-center pb-3 mt-10" v-if="fetchingGracePullDetails">
      <LoadingSpinner color-class="text-tb-blue"></LoadingSpinner>
    </div>
    <div v-else class="mt-10 max-w-fit overflow-auto">
      <p class="text-gray-700">Action:</p>
      <p class="font-semibold">{{ this.gracePullDetails?.lead_schedule?.action?.name }}</p>
      <p class="mt-4 xt-gray-7">Scheduled Datetime (EST):</p>
      <p class="font-semibold">{{ this.gracePullDetails?.grace_pulled_lead_schedule?.original_lead_schedule_data?.agent_scheduled_datetime_literal }}</p>
      <p class="mt-4 xt-gray-7">Pulled Datetime (EST):</p>
      <p class="font-semibold">{{ this.gracePullDetails?.created_at_literal }}</p>
      <p class="mt-4 xt-gray-7">Grace Hours:</p>
      <p class="font-semibold">{{ this.gracePullDetails?.lead_schedule?.action_schedule_map?.grace_pull_setting?.grace_hours }}</p>
    </div>

  </Modal>
</template>

<script>
import LeadViewNoteForm from "@/Components/partials/LeadViewNoteForm.vue";
import helpers from "../helper";
import LeadSalesTable from "./partials/LeadSalesTable.vue";
import LeadLoginTable from "./partials/LeadLoginTable.vue";
import LeadViewInfo from "@/Components/partials/LeadViewInfo.vue";
import LeadViewFiles from "@/Components/partials/LeadViewFiles.vue";
import LoadingSpinner from "@/Components/LoadingSpinner.vue";
import LeadDependants from "@/Components/partials/LeadDependants.vue";
import LeadDependantsTable from "@/Components/partials/LeadDependantsTable.vue";
import LeadDependantForm from "@/Components/partials/LeadDependantForm.vue";
import LeadViewQuotesheets from "@/Components/partials/LeadViewQuotesheets.vue";
import AppQuoteMatchCard from "@/Components/AppQuoteMatchCard.vue";
import LeadQuestionnairesTable from "@/Components/partials/LeadQuestionnairesTable.vue";
import Modal from "@/Components/Modal.vue";
import {Link, usePage} from "@inertiajs/inertia-vue3";
import {PlusCircleIcon, FlagIcon, TrashIcon, XIcon, DuplicateIcon, ArrowDownIcon} from "@heroicons/vue/outline";
import moment from "moment-timezone";
import Swal from "sweetalert2";
import {Inertia} from "@inertiajs/inertia";
import EmailTemplatePreview from "@/Components/partials/EmailTemplatePreview.vue";
import {ElCheckbox, ElDropdown, ElDropdownMenu, ElDropdownItem, ElButton, ElTooltip} from "element-plus";
import _ from "lodash";
import LeadMedicationsAndDoctors from "@/Components/partials/LeadMedicationsAndDoctors.vue";
import LeadChildLeadsTable from "@/Components/partials/LeadChildLeadsTable.vue";
import LeadBillingForm from "@/Components/partials/LeadBillingForm.vue";
import AgentAndLeadStates from "@/Components/partials/AgentAndLeadStates.vue";
import AgentAndLeadCarriers from "@/Components/partials/AgentAndLeadCarriers.vue";
import LeadCampaigns from "@/Components/partials/LeadCampaigns.vue";
import LeadIntakeForms from "@/Components/partials/LeadIntakeForms.vue";
import ActionHistoryHstSyncStatus from "@/Components/ActionHistoryHstSyncStatus.vue";
import LeadActionHistoryTab from "@/Pages/Leads/Partials/LeadActionHistoryTab.vue";
import {ref} from "vue";

export default {
  name: "AppLeadView",

  components: {
    LeadActionHistoryTab,
    LeadCampaigns,
    LeadIntakeForms,
    ActionHistoryHstSyncStatus,
    AgentAndLeadCarriers,
    AgentAndLeadStates,
    LeadMedicationsAndDoctors,
    LeadBillingForm,
    LeadChildLeadsTable,
    LeadQuestionnairesTable,
    ElCheckbox, ElDropdown, ElDropdownMenu, ElDropdownItem, ElButton, ElTooltip,
    EmailTemplatePreview,
    LeadViewNoteForm,
    LeadViewInfo,
    LeadViewFiles,
    LeadSalesTable,
    LeadLoginTable,
    LoadingSpinner,
    LeadDependants,
    LeadDependantsTable,
    LeadDependantForm,
    LeadViewQuotesheets,
    AppQuoteMatchCard,
    PlusCircleIcon, FlagIcon, TrashIcon, XIcon, DuplicateIcon, ArrowDownIcon,
    Modal,
    Link,
  },

  props: {
    timezone: {
        type: String,
        default: 'America/New_York'
    },
    lead: {
      type: Object,
      required: true,
    },
    isDnc: Boolean,
    leadActionHistory: Array,
    nextLeadSchedule: Object,
    states: {
      type: Array,
      required: true,
    },

    leadDependants: {
      type: Array,
    },

    dependantRoles: {
      type: Array,
      required: true,
    },

    roles: Object,

    policies: {},

    products: {},

    quotesheets: {
      type: Object,
      required: true,
    },

    enrollees: {
      type: Array,
      required: true,
    },

    agents: {},

    splitPercentages: {
      type: Array,
      required: true,
    },

    carriersData: {},
    applicants: {
      type: Array,
      required: true,
    },
    nextLeadScheduleList: {
      type: Array,
      required: true,
    },
    clicktocall: {
      type: String,
      default: null,
    },
    logins: {
      type: Object,
      default: true,
    },
    totalPremium: {
      type: [Number, String],
    },
    hst_available: {
      type: Boolean,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    questionnaires: {
      type: Object,
    },
    leadTypeQuestionnaires: {
      type: Object,
    },
    hst_age_locked: Boolean,
    childLeads: {},
    leadParentOptions: {},
    leadBillingInfo: {},
    dependantCompanyRoles: {},
    agency_carriers: {},
    leadCampaigns: {},
    leadCampaignsGlobalExclusion: {},
    leadOptedOutOfAgentEmails: {},
    hstActionSyncStatuses: {},
    languages: {},
    reportedExternalDncPhone1: {},
    reportedExternalDncPhone2: {},
  },
  emits: ['hstAgeLockedChanged'],
  data() {
    return {
      tabs: [
        {key: 'lead-info', label: 'Lead Info'},
        {key: 'action-history', label: 'Action History'},
        {key: 'quotesheets', label: 'Quotesheets', when: () => this.lead.lead_category === 'commercial' && !this.isOnlyReferralRole},
        {key: 'policies', label: 'Policies', when: () => this.lead.lead_category === 'commercial'},
        {key: 'files', label: 'Files', when:() => !this.isOnlyReferralRole},
        {key: 'login', label: 'Logins', when: () => this.lead.lead_category === 'commercial' && !this.isOnlyReferralRole},
        {
        key: 'questionnaires',
        label: 'Questionnaires',
        when: () => {
          return this.questionnaires?.data?.some(questionnaire => questionnaire.user_id === this.lead.user_id) && !this.isOnlyReferralRole;
          }
        },
        {key: 'medications', label: 'Medical Info', when: () => this.lead.lead_category === 'commercial' && !this.isOnlyReferralRole},
        {
        key: 'lead-billing',
        label: 'Billing',
        when: () => {
          return this.lead.has_type_marked_as_group && !this.isOnlyReferralRole;
          }
        },
        {
        key: 'child-leads',
        label: `Child Leads (${this.childLeads?.data?.length ?? 0})`,
        when: () => {
          return this.lead.has_type_marked_as_group && !this.isOnlyReferralRole;
          }
        },
        { key: 'campaigns', label: 'Campaigns', when: () => this.lead.lead_category === 'commercial' && !this.isOnlyReferralRole },
        { key: 'forms', label: 'Forms', when: () => this.lead.lead_category === 'commercial' && !this.isOnlyReferralRole },
        {
        key: 'state-licenses',
        label: 'States',
        when: () => {
            return this.lead.lead_category === 'recruitment' && ![34].includes(this.lead.lead_status_id);
          }
        },
        {
        key: 'carriers',
        label: 'Carriers',
        when: () => {
          return this.lead.lead_category === 'recruitment' && ![34].includes(this.lead.lead_status_id);
          }
        },
      ],
      loading: false,
      editMode: false,
      currentTab: 'lead-info',
      dependantModalVisible: false,
      createLeadNoteModalVisible: false,
      dependantToEdit: null,
      showEmailContentModal: false,
      fetchingEmailContent: false,
      emailContent: null,
      showGracePullDetailsModal: false,
      fetchingGracePullDetails: false,
      gracePullDetails: null,
      dependants: _.cloneDeep((this.leadDependants)),
      data_hst_age_locked: this.hst_age_locked,
    }
  },
  setup() {
      const leadSalesLoading = ref(false);
      const leadSalesLoaded = ref(false);
      return {
          leadSalesLoading, leadSalesLoaded,
      }
  },
  mounted() {
    this.checkLockForHstLeads();
    this.initializeTabFromUrl();
  },
  methods: {
    usePage,
    moment_wrapper: moment,
    checkLockForHstLeads() {
      if (!this.data_hst_age_locked && this.lead?.hst_lead_id) {
        axios.get(route('leads.hst-lock-check', {lead: this.lead.id}))
          .then(r => {
            if (r.data?.hst_age_locked && r.data.hst_age_locked === true) {
              this.data_hst_age_locked = true;
              this.lead.phone = 'HST Aged Locked';
              this.lead.phone_2 = 'HST Aged Locked';
              this.lead.email = 'HST Aged Locked';
              this.lead.email_2 = 'HST Aged Locked';
              $notify({
                type: 'info',
                message: 'Lead is HST Age Locked',
              });
              this.$emit('hstAgeLockedChanged', this.data_hst_age_locked);
            }
          }).catch(e => {
            throw e;
        });
      }
    },
    handleShowEditMode(payload) {
      if (payload.fieldId) {
        this.toggleEditMode(payload.fieldId, payload.focusDependants)
      } else {
        this.toggleEditMode();
      }
    },

    markLeadAsDNC() {
      Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, mark as DNC!'
      }).then((result) => {
        if (result.isConfirmed) {
          Inertia.post(route('leads.dnc', {lead: this.lead.id}), {}, {
            preserveScroll: true,
            preserveState: true,
            only: ['lead', 'leadActionHistory', 'isDnc'],
            onSuccess: () => {
              $notify.success({
                title: 'Success',
                message: 'Lead has been marked as DNC',
              });
              this.$emit('leadStatusUpdated', {is_dnc: true});
            },
            onError: () => {
              $notify.error({
                title: 'Error',
                message: 'An error occurred while marking lead as DNC',
              });
            }
          });
        }
      });
    },

    toggleEditMode(scrollToFieldId = 'first_name', focusDependants = false) {
      this.editMode = !this.editMode;

      if (this.editMode) {
        this.$nextTick(() => {
          if (focusDependants) {
            document.getElementById(scrollToFieldId)?.scrollIntoView({
              behavior: "smooth",
              block: "end",
              inline: "nearest"
            });
          } else {
            setTimeout(() => {
              document.getElementById(scrollToFieldId)?.focus();
            }, 50)
          }
        });
      }
    },

    setCurrentTab(tabKey) {
      this.currentTab = tabKey;
    },

    initializeTabFromUrl() {
      // Read the 'tab' query parameter from the URL
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get('tab');
      
      if (tabParam) {
        // Check if the tab exists in our tabs array and is visible
        const tabExists = this.visibleTabs.some(tab => tab.key === tabParam);
        if (tabExists) {
          this.currentTab = tabParam;
        }
      }
    },

    confirmDelete() {
      this.loading = true;
      Swal.fire({
        title: 'Are you sure?',
        text: 'You will not be able to undo this.',
        icon: 'warning',
        confirmButtonText: "Yes, I'm sure.",
        showCancelButton: true,
        confirmButtonColor: '#B91C1C',
        cancelButtonColor: '#899CC1'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteLead();
        }
      })
    },

    deleteLead() {
      axios.delete(`/leads/${this.lead.id}`)
      .then((response) => {
        if (response && response.data && response.data.redirect_url) {
          this.$inertia.get(response.data.redirect_url);
        }
      })
      .catch((error) => {
        Swal.fire({
          text: 'Unknown error occurred, please try again.',
          icon: 'error'
        });

        console.log("AppLeadView - handleErroredSubmission", error.response);
      })
      .finally(() => {
        this.loading = false;
      });
    },

    submitLeadInfoForm() {
      this.$refs['lead-view-info-tab'].submit();
    },

    handleDependantsUpdated(event) {
      this.dependantToEdit = null
      this.dependantModalVisible = false;
      this.dependants = this.leadDependants
    },

    showDependantEditForm(payload) {
      this.dependantToEdit = payload.dependant;

      setTimeout(() => {
        this.dependantModalVisible = true;
      }, 50)
    },

    closeDependantFormModal() {
      this.dependantModalVisible = false
      this.dependantToEdit = null;
    },

    handleNoteAdded() {
      this.createLeadNoteModalVisible = false;
      Inertia.reload({
        only: ['leadActionHistory'],
        preserveState: true,
        preserveScroll: true,
      });
    },

    showComposeSmsForm(payload) {
      Inertia.get(route('leads.custom-sms.compose', payload.leadId), {
        phone: payload.phone
      });
    },
    showComposeEmailForm(payload) {
      Inertia.get(route('leads.custom-email.compose', payload.leadId), {
        email: payload.email
      });
    },
    showEmailMessageContent(emailMessageId) {
      if (this.fetchingEmailContent) return;
      this.showEmailContentModal = true;
      this.fetchingEmailContent = true;
      axios
          .get(route('email-messages.preview', emailMessageId))
          .then((response) => {
              this.emailContent = response.data?.rendered;
          })
          .catch(error => {
          })
          .finally(() => {
              setTimeout(() => {
                  this.fetchingEmailContent = false;
              }, 300)
          })
    },
    showEmailTemplateContent(emailTemplateId) {
        if (this.fetchingEmailContent) return;
        this.showEmailContentModal = true;
        this.fetchingEmailContent = true;
        axios
            .get(route('email-templates.preview', [emailTemplateId, this.lead.id]))
            .then((response) => {
                this.emailContent = response.data?.rendered;
            })
            .catch(error => {
            })
            .finally(() => {
                setTimeout(() => {
                    this.fetchingEmailContent = false;
                }, 300)
            })
    },
    showGracePullDetails(grace_pull_log_id) {
      if (this.fetchingGracePullDetails) return;
      this.showGracePullDetailsModal = true;
      this.fetchingGracePullDetails = true;
      axios
        .get(route('grace-pull.log-details', grace_pull_log_id))
        .then(r => {
          this.gracePullDetails = r.data;
          if (this.gracePullDetails.grace_pulled_lead_schedule) {
              this.gracePullDetails.grace_pulled_lead_schedule.original_lead_schedule_data = JSON.parse(this.gracePullDetails.grace_pulled_lead_schedule.original_lead_schedule_data);
          }
          console.log(this.gracePullDetails);
        })
        .finally(() => {
          setTimeout(() => {
            this.fetchingGracePullDetails = false;
          }, 300);
        });
    },
    closeGracePullDetails() {
      this.gracePullDetails = null;
      this.showGracePullDetailsModal = false;
    },
    closeEmailContentModal() {
        this.showEmailContentModal = false;
        setTimeout(() => {
            this.emailContent = null;
        }, 300)
    },
    performExtraOption(extraOption, only = []) {
      // Input initialization
      let optionEndpoint = extraOption.endpoint;
      let optionMethod   = extraOption.method.toUpperCase();
      let optionData     = extraOption.data;

      // Request initialization
      let requestOptions = {
          preserveScroll: true,
          onSuccess: () => {
              $notify.success({
                  title: 'Success',
                  message: extraOption.messages?.success ?? 'Action completed',
                  showClose: true,
              });
          },
          onError: e => {
              console.log(e);
              let errorMessage = 'asdasdsa';
              $notify.error({
                  title: 'Error',
                  message: extraOption.messages?.error ? extraOption.messages.error.replace(/\{e\}/, errorMessage) : errorMessage,
                  showClose: true,
              });
          },
          onFinish: () => {},
      }

      if (only && only.length > 0) {
          requestOptions.only = only;
      }

      // DO Request
      switch(optionMethod) {
          case 'POST':
              Inertia.post(optionEndpoint, optionData, requestOptions);
              break;
          default:
              throw new Error("Method not implemented: " + optionMethod);
      }
    },
    handleRefreshDependants(event) {
        this.dependants = event;
    },
  },
  computed: {
      isAdmin() {
        return this.roles.some(i => ['Super Admin', 'Agency Admin'].indexOf(i.name) > -1);
      },
      isRecruiterAdmin() {
        return this.roles.some(i => ['Recruiter Admin'].indexOf(i.name) > -1);
      },
      isOnlyReferralRole() {
        return this.roles?.length < 2 && this.roles[0].name === 'Referral Program User';
      },
      canEdit() {
          return ! this.isOnlyReferralRole;
      },
      canCreateNote() {
          return this.canEdit;
      },
      visibleTabs() {
        return this.tabs.filter(i => {
          return typeof i.when === 'undefined' || typeof i.when !== 'function' || i.when();
        });
      },
  }
}
</script>

<style scoped lang="scss">
li.active {
  width: unset !important;
}
</style>
<style lang="scss">
#filtersDropdown {
    .el-checkbox__label {
        color: rgb(17, 24, 39);
    }
    .el-checkbox__input {
        &.is-checked {
            .el-checkbox__inner {
                background-color: #899CC1;
            }
        }
    }
}
</style>
